import { useMemo, useState } from "react";
import {
  Activity,
  ArrowLeft,
  FileText,
  Loader2,
  Receipt,
  Shield,
  Truck,
  Zap,
  type LucideIcon,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import {
  usePolicy,
  useSyncSinglePolicy,
  usePolicySyncStatus,
  useInsuredPolicies,
  useFlagPolicyAor,
  useRestorePolicyAor,
} from "@/lib/api";
import { getPolicySyncIneligibleReason } from "@shared/policy-sync-eligibility";
import { useToast } from "@/hooks/use-toast";
import type { Insured, Policy } from "@shared/schema";
import { EditPolicyDialog } from "./EditPolicyDialog";
import { PolicyDetailHeader } from "./PolicyDetailHeader";
import { PolicySyncBanner } from "./PolicySyncBanner";
import { PolicyCoveragesTab } from "./PolicyCoveragesTab";
import {
  PolicyActivitiesTab,
  usePolicyActivityEvents,
} from "./PolicyActivitiesTab";
import { PolicyBillingTab } from "./PolicyBillingTab";
import { PolicyAssetsTab } from "./PolicyAssetsTab";
import { buildCoverageLines, onPolicyCoverageLines } from "@/lib/policy-coverages";

type DetailTabKey = "coverages" | "activities" | "billing" | "quotes" | "assets";

interface DetailTab {
  key: DetailTabKey;
  label: string;
  icon: LucideIcon;
}

const DETAIL_TABS: DetailTab[] = [
  { key: "coverages", label: "Coverages", icon: Shield },
  { key: "activities", label: "Activities", icon: Activity },
  { key: "billing", label: "Billing", icon: Receipt },
  { key: "quotes", label: "Quotes", icon: Zap },
  { key: "assets", label: "Assets", icon: Truck },
];

interface Props {
  policyId: string;
  insured: Insured;
  fallbackPolicy?: Policy;
  onBack: () => void;
  // CSR-2327 — the AOR experience flag, read once by the insured page.
  showAor?: boolean;
}

export function InsuredPolicyDetail({
  policyId,
  insured,
  fallbackPolicy,
  onBack,
  showAor = false,
}: Props) {
  const { data: fetchedPolicy, isLoading, isError } = usePolicy(policyId);
  const policy = fetchedPolicy ?? fallbackPolicy;

  if (isLoading && !policy) {
    return (
      <div className="space-y-4" data-testid="insured-policy-detail-loading">
        <BackButton onBack={onBack} />
        <div className="flex justify-center py-16">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" aria-hidden="true" />
        </div>
      </div>
    );
  }

  if (isError || !policy) {
    return (
      <div className="space-y-4" data-testid="insured-policy-detail-error">
        <BackButton onBack={onBack} />
        <Card className="p-12 text-center">
          <FileText className="w-10 h-10 mx-auto mb-3 text-muted-foreground/50" aria-hidden="true" />
          <p className="text-base font-medium">Policy not found</p>
          <p className="text-sm text-muted-foreground mt-1">
            The policy you selected may have been removed or is no longer accessible.
          </p>
        </Card>
      </div>
    );
  }

  return (
    <Loaded policy={policy} insured={insured} onBack={onBack} showAor={showAor} />
  );
}

function Loaded({
  policy,
  insured,
  onBack,
  showAor,
}: {
  policy: Policy;
  insured: Insured;
  onBack: () => void;
  showAor: boolean;
}) {
  const [active, setActive] = useState<DetailTabKey>("coverages");
  const [editOpen, setEditOpen] = useState(false);
  const { events: policyActivityEvents } = usePolicyActivityEvents({
    policyId: policy.id,
    insuredId: insured.id,
    policy,
    showAor,
  });
  const counts = usePolicyDetailCounts(policy, policyActivityEvents.length);
  const { toast } = useToast();

  // Manual single-policy refresh, mirroring the standalone policy page.
  // usePolicySyncStatus owns the completion toast + cache invalidation on
  // the syncing → done transition, so we only fire the mutation here.
  const { data: syncStatus } = usePolicySyncStatus(policy.id);
  const isSyncing = syncStatus?.syncing === true;
  const syncMutation = useSyncSinglePolicy(policy.id);
  const busy = syncMutation.isPending || isSyncing;

  // CSR-2169 — shared eligibility rule (same helper the server fan-out
  // applies): supported carrier AND the most recent term of its policy
  // number. Sibling terms come from the insured's already-cached policy list
  // (the page loads it via this same hook, so this is a cache read). Until
  // the list is available the policy ranks against itself alone — the carrier
  // rule still applies, and a lone term is always most-recent.
  const { data: insuredPolicies } = useInsuredPolicies(insured.id);
  const syncIneligibleReason = useMemo(
    () => getPolicySyncIneligibleReason(policy, insuredPolicies?.data ?? [policy]),
    [policy, insuredPolicies],
  );

  // CSR-2327 — "Customer AOR'd away" and "Access restored". Neither touches
  // Sync now: an agent can still check whether access is back.
  const flagAor = useFlagPolicyAor();
  const restoreAor = useRestorePolicyAor();
  const aorPending = flagAor.isPending || restoreAor.isPending;

  const handleFlagAor = () => {
    flagAor.mutate(
      { id: policy.id },
      {
        onSuccess: () =>
          toast({
            title: "Policy flagged as AOR'd away",
            description:
              "The automation can no longer access this policy, so its data will not be updated.",
          }),
        onError: (err: Error) =>
          toast({
            title: "Could not flag the policy",
            description: err.message,
            variant: "destructive",
          }),
      },
    );
  };

  const handleRestoreAor = () => {
    restoreAor.mutate(
      { id: policy.id },
      {
        onSuccess: () =>
          toast({
            title: "AOR'd away flag removed",
            description: "Automatic updates can resume for this policy.",
          }),
        onError: (err: Error) =>
          toast({
            title: "Could not restore access",
            description: err.message,
            variant: "destructive",
          }),
      },
    );
  };

  const handleRefresh = () => {
    if (busy || syncIneligibleReason) return;
    syncMutation.mutate(undefined, {
      onSuccess: () =>
        toast({
          title: "Sync started",
          description: "Fetching latest policy data from the carrier via Data Center.",
        }),
      onError: (err: Error) =>
        toast({ title: "Sync failed", description: err.message, variant: "destructive" }),
    });
  };

  return (
    <div className="space-y-4" data-testid="insured-policy-detail">
      <BackButton onBack={onBack} />
      <PolicyDetailHeader
        policy={policy}
        onEdit={() => setEditOpen(true)}
        onRefresh={handleRefresh}
        refreshing={busy}
        syncing={isSyncing}
        syncIneligibleReason={syncIneligibleReason}
        lastSyncOutcome={syncStatus?.lastSyncOutcome ?? null}
        lastSyncError={syncStatus?.lastSyncError ?? null}
        lastSyncOutcomeAt={syncStatus?.lastSyncOutcomeAt ?? null}
        showAor={showAor}
        onFlagAor={handleFlagAor}
        onRestoreAor={handleRestoreAor}
        aorPending={aorPending}
      />
      <PolicySyncBanner
        outcome={syncStatus?.lastSyncOutcome ?? null}
        error={syncStatus?.lastSyncError ?? null}
      />
      <EditPolicyDialog policy={policy} open={editOpen} onOpenChange={setEditOpen} onDeleted={onBack} />
      <Card className="overflow-hidden">
        <div
          role="tablist"
          aria-label="Policy detail sections"
          className="flex gap-1 px-6 pt-3 border-b border-border overflow-x-auto"
        >
          {DETAIL_TABS.map((tab) => {
            const isActive = tab.key === active;
            const Icon = tab.icon;
            return (
              <button
                key={tab.key}
                type="button"
                role="tab"
                aria-selected={isActive}
                onClick={() => setActive(tab.key)}
                className={cn(
                  "inline-flex cursor-pointer items-center gap-2 px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 -mb-px transition-colors",
                  isActive
                    ? "border-foreground text-foreground"
                    : "border-transparent text-muted-foreground hover:text-foreground"
                )}
                data-testid={`policy-detail-tab-${tab.key}`}
              >
                <Icon className="w-4 h-4" aria-hidden="true" />
                {tab.label}
                <span
                  className={cn(
                    "ml-1 inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full text-[11px] font-medium",
                    isActive
                      ? "bg-foreground text-background"
                      : "bg-muted text-muted-foreground"
                  )}
                >
                  {counts[tab.key]}
                </span>
              </button>
            );
          })}
        </div>
        <div className="p-6">
          {active === "coverages" && <PolicyCoveragesTab policy={policy} />}
          {active === "activities" && (
            <PolicyActivitiesTab
              policyId={policy.id}
              insuredId={insured.id}
              policy={policy}
              showAor={showAor}
            />
          )}
          {active === "billing" && <PolicyBillingTab />}
          {active === "quotes" && (
            <div className="py-12 text-center" data-testid="policy-quotes-placeholder">
              <Zap className="w-10 h-10 mx-auto mb-3 text-muted-foreground/40" aria-hidden="true" />
              <p className="text-base font-medium text-foreground">Quotes coming soon</p>
              <p className="text-sm text-muted-foreground mt-1">
                Endorsement quotes attached to this policy will appear here.
              </p>
            </div>
          )}
          {active === "assets" && (
            <PolicyAssetsTab policy={policy} insured={insured} />
          )}
        </div>
      </Card>
    </div>
  );
}

function BackButton({ onBack }: { onBack: () => void }) {
  return (
    <button
      type="button"
      onClick={onBack}
      className="inline-flex cursor-pointer items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
      data-testid="insured-policy-detail-back"
    >
      <ArrowLeft className="w-4 h-4" aria-hidden="true" />
      Back to policies
    </button>
  );
}

function usePolicyDetailCounts(
  policy: Policy,
  policyActivityCount: number,
): Record<DetailTabKey, number> {
  return useMemo(() => {
    // CSR-2148 — mirrors the removed-asset filters below: a coverage the
    // carrier dropped still renders on the tab, but it is not counted.
    const coverageLines = onPolicyCoverageLines(
      buildCoverageLines(policy.coverages as never),
    );
    const vehicles = Array.isArray(policy.vehicles)
      ? (policy.vehicles as Array<{ removedAt?: string | null }>).filter((v) => !v.removedAt).length // CSR-776
      : 0;
    const trailers = Array.isArray(policy.trailers)
      ? (policy.trailers as Array<{ removedAt?: string | null }>).filter((t) => !t.removedAt).length // CSR-776
      : 0;
    const drivers = Array.isArray(policy.drivers)
      ? (policy.drivers as Array<{ removedAt?: string | null }>).filter((d) => !d.removedAt).length // CSR-776
      : 0;
    return {
      coverages: coverageLines.length,
      activities: policyActivityCount,
      billing: 0,
      quotes: 0,
      assets: vehicles + trailers + drivers,
    };
  }, [
    policy.coverages,
    policy.vehicles,
    policy.trailers,
    policy.drivers,
    policyActivityCount,
  ]);
}
